import axios from 'axios';
import config from 'config/config';
import { TableGroup, LanSettings } from 'types/common';
import { getMockBlockedLocations } from './intraServiceMocks';

const MOCK_TABLE_GROUPS: TableGroup[] = [
    { name: 'A', groupSize: 8, style: { float: 'left', top: '40px', left: '40px' } },
    { name: 'B', groupSize: 8, style: { float: 'left', top: '40px', left: '240px' } },
    { name: 'C', groupSize: 8, style: { float: 'left', top: '40px', left: '440px' } }
];

const useMocks = () => config.USE_MOCK_API === 'true';

class TableService {
    host = config.API_BASE_URL;
    apiPath = '/v1/lanbooking';

    book(email?: string, code?: string, location?: string) {
        if (email && code && location) {
            return axios.post(`${this.host}${this.apiPath}`, {
                email: email,
                code: code,
                location: location
            });
        }

        return Promise.reject('email, code or location missing');
    }

    free(email?: string, code?: string) {
        if (email && code) {
            return axios.post(`${this.host}${this.apiPath}`, {
                email: email,
                code: code,
                location: null
            });
        }

        return Promise.reject('email, code or location missing');
    }

    isBooked(location: string) {
        return axios.get(`${this.host}${this.apiPath}/isbooked/` + location);
    }

    allBooked() {
        return axios.get(`${this.host}${this.apiPath}/booked`);
    }

    myBooking(email: string, code: string) {
        const params = new URLSearchParams();
        params.append('email', email);
        params.append('code', code);

        return axios.get(`${this.host}${this.apiPath}/check`, { params: params });
    }

    sendEmails() {
        const code = 'lan123Send456Mail';
        const params = new URLSearchParams();
        params.append('code', code);
        return axios.get(`${this.host}${this.apiPath}/sendEmails`, { params: params });
    }

    async getSettings(): Promise<LanSettings> {
        return axios.get(`${this.host}${this.apiPath}/settings`).then(res => res.data);
    }

    /**
     * Get all table group configurations
     * Currently returns mock data, will be replaced with REST API call:
     * return axios.get(`${this.host}${this.apiPath}/tablegroups`);
     */
    async getAllTableGroups(): Promise<TableGroup[]> {
        try {
            return await axios.get(`${this.host}${this.apiPath}/tablegroups`).then(res => res.data);
        } catch (error) {
            if (useMocks()) {
                return MOCK_TABLE_GROUPS;
            }
            throw error;
        }
    }

    async getBlockedLocations(): Promise<string[]> {
        try {
            return await axios.get(`${this.host}${this.apiPath}/blocked`).then(res => res.data ?? []);
        } catch (error) {
            if (useMocks()) {
                return getMockBlockedLocations().map(item => item.location);
            }
            throw error;
        }
    }

    /**
     * Get table group by name
     * Currently returns mock data, will be replaced with REST API call:
     * return axios.get(`${this.host}${this.apiPath}/tablegroups/${name}`);
     */
    async getTableGroupByName(name: string): Promise<TableGroup[]> {
        // TODO: Replace with actual API call when backend is ready
        // return axios.get(`${this.host}${this.apiPath}/tablegroups/${name}`).then(res => res.data);

        const allGroups = await this.getAllTableGroups();
        return allGroups.filter(group => group.name === name);
    }

    /**
     * Update table group configuration
     * Currently returns mock success, will be replaced with REST API call:
     * return axios.put(`${this.host}${this.apiPath}/tablegroups/${id}`, tableGroup);
     */
    async updateTableGroup(_id: string, _tableGroup: TableGroup): Promise<void> {
        // TODO: Replace with actual API call when backend is ready
        // return axios.put(`${this.host}${this.apiPath}/tablegroups/${id}`, tableGroup);

        return Promise.resolve();
    }
}

export default new TableService();
